'use client';

import { useState } from 'react';

import { AnimatePresence, m } from 'framer-motion';

import BackgroundSlide from './BackgroundSlide';
import BlocksSlide from './BlocksSlide';
import { slideVariants } from './constants';
import SplitSlide from './SplitSlide';
import type { MainSliderItem, MainSliderProps } from './types';
import { cn } from './utils';

const renderActiveLayout = (slide: MainSliderItem) => {
  const layout = slide.layout ?? 'split';

  if (layout === 'background') return <BackgroundSlide slide={slide} />;
  if (layout === 'blocks') return <BlocksSlide slide={slide} />;

  return <SplitSlide slide={slide} />;
};

const MainSlider = ({
  slides,
  className = '',
  heightClassName = 'h-[560px]',
}: MainSliderProps) => {
  const [[activeIndex, direction], setActiveSlide] = useState([0, 0]);

  const activeSlide = slides?.[activeIndex];

  const goPrev = () => {
    const slidesLength = slides?.length ?? 0;
    if (slidesLength <= 1) return;

    setActiveSlide(([current]) => [
      current === 0 ? slidesLength - 1 : current - 1,
      -1,
    ]);
  };

  const goNext = () => {
    const slidesLength = slides?.length ?? 0;
    if (slidesLength <= 1) return;

    setActiveSlide(([current]) => [
      current === slidesLength - 1 ? 0 : current + 1,
      1,
    ]);
  };

  if (!activeSlide) return null;

  return (
    <section
      className={cn('content-wrapper', className)}
      data-hide-floating-actions="true"
    >
      <div
        className={cn('relative overflow-hidden rounded-2xl', heightClassName)}
      >
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <m.div
            key={activeSlide.id}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              duration: 0.45,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="h-full"
          >
            {renderActiveLayout(activeSlide)}
          </m.div>
        </AnimatePresence>
      </div>

      {(slides?.length ?? 0) > 1 && (
        <div className="mt-7 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous slide"
            className="bg-surface-slider-control text-text-slider-body hover:bg-surface-slider-control-hover flex size-10 cursor-pointer items-center justify-center rounded-full transition"
          >
            ←
          </button>

          <button
            type="button"
            onClick={goNext}
            aria-label="Next slide"
            className="bg-surface-slider-control text-text-slider-body hover:bg-surface-slider-control-hover flex size-10 cursor-pointer items-center justify-center rounded-full transition"
          >
            →
          </button>
        </div>
      )}
    </section>
  );
};

export default MainSlider;
